import { ApiError } from '@central/api';
import { Shell, color, navItemStyle } from '@central/ui';
import { Badge, Button } from '@mantine/core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, Outlet, useNavigate, useRouteContext } from '@tanstack/react-router';
import { api, unreadCountQuery } from '../api';
import { Brand } from './Brand';
import { Notifications } from './Notifications';
import { navItems } from './nav';

export function AppLayout() {
  const { me } = useRouteContext({ from: '/app' });
  const qc = useQueryClient();
  const navigate = useNavigate();
  const unread = useQuery(unreadCountQuery).data?.threads ?? 0;
  // A 401 means the session is already gone (the query client's 401 handler
  // clears the cache and goes to sign in). Any other failure leaves the
  // server session alive, so the user stays here and may retry: logout is
  // idempotent.
  const signOut = useMutation({
    mutationFn: () => api.auth.logout(),
    onSuccess: () => {
      qc.clear();
      void navigate({ to: '/sign-in' });
    },
  });
  const failed =
    signOut.error !== null && !(signOut.error instanceof ApiError && signOut.error.status === 401);
  return (
    <Shell
      variant="portal"
      brand={<Brand customerName={me.customer.name} />}
      nav={navItems.map((item) => (
        <Link
          key={item.to}
          to={item.to}
          activeOptions={{ exact: item.to === '/' }}
          style={navItemStyle('portal', false)}
          activeProps={{ style: navItemStyle('portal', true), 'aria-current': 'page' }}
        >
          {item.label}
          {item.to === '/messages' && unread > 0 && (
            <Badge size="sm" ml="auto" aria-label={`${unread} unread`}>
              {unread}
            </Badge>
          )}
        </Link>
      ))}
      footer={
        <>
          <div>
            {me.user.name} · {me.user.role}
          </div>
          <Button
            variant="subtle"
            size="compact-sm"
            fullWidth
            justify="flex-start"
            mt={10}
            loading={signOut.isPending}
            onClick={() => signOut.mutate()}
          >
            Sign out
          </Button>
          {failed && (
            <div role="alert" style={{ marginTop: 8, color: color.text }}>
              Couldn’t sign you out. You’re still signed in; try again.
            </div>
          )}
        </>
      }
    >
      <Notifications />
      <Outlet />
    </Shell>
  );
}
